import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { MEASURE_FIELDS } from "@/lib/measure";

const LABELS = {
  AWAITING_PAYMENT: "بانتظار الدفع", AWAITING_CASH_PAYMENT: "بانتظار الدفع نقداً", PAID: "تم الدفع",
  MEASUREMENTS_REVIEW: "مراجعة المقاسات", IN_PRODUCTION: "قيد التفصيل", READY: "جاهز",
  OUT_FOR_DELIVERY: "خرج للتوصيل", SHIPPED: "تم الشحن", DELIVERED: "تم التسليم",
  CANCELLED: "ملغي", REFUND_PENDING: "بانتظار الاسترجاع", REFUNDED: "تم الاسترجاع",
};
const NEXT = ["MEASUREMENTS_REVIEW", "IN_PRODUCTION", "READY", "OUT_FOR_DELIVERY", "SHIPPED", "DELIVERED", "CANCELLED", "REFUND_PENDING", "REFUNDED"];

function Measurements({ it }) {
  if (!it.measurements) return null;
  return (
    <div className="text-[11px] text-text-muted mt-1" data-testid="order-measurements">
      {MEASURE_FIELDS.map((f) => `${f.ar} ${it.measurements[f.key] ?? "—"}`).join(" · ")} سم
      {it.notes ? <div className="mt-0.5">ملاحظات: {it.notes}</div> : null}
    </div>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [ns, setNs] = useState("");
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [payF, setPayF] = useState("");

  const load = () => { setLoading(true); api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => orders.filter((o) => {
    if (statusF && o.status !== statusF) return false;
    if (payF && o.payment_method !== payF) return false;
    if (q) {
      const hay = `${o.order_ref || o.order_no} ${o.customer_name} ${o.phone}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [orders, q, statusF, payF]);

  const confirmCash = async (o) => {
    try { await api.post(`/staff/orders/${o.id}/confirm-cash`); toast.success("تم تأكيد الدفع النقدي"); load(); setOpen(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (o) => {
    if (!ns) return;
    if ((ns === "CANCELLED" || ns === "REFUND_PENDING") && !note.trim()) { toast.error("يرجى إدخال سبب التغيير"); return; }
    try { await api.put(`/staff/orders/${o.id}/status`, { status: ns, note }); toast.success("تم تحديث الحالة"); setNote(""); setNs(""); load(); setOpen(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-5 md:p-8">
      <h1 className="font-display text-3xl mb-6">طلبات الموقع</h1>

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث برقم الطلب أو الهاتف…" data-testid="order-search"
            className="w-full bg-white border border-border ps-10 pe-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="order-filter-status" className="bg-white border border-border px-3 py-2.5 text-sm">
          <option value="">كل الحالات</option>{Object.entries(LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={payF} onChange={(e) => setPayF(e.target.value)} data-testid="order-filter-pay" className="bg-white border border-border px-3 py-2.5 text-sm">
          <option value="">كل طرق الدفع</option><option value="knet">كي نت</option><option value="cash">نقداً</option>
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 bg-surface animate-pulse" />)}</div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="grid gap-3 md:hidden">
            {filtered.map((o) => (
              <div key={o.id} className="bg-white border border-border p-4" data-testid={`admin-order-card-${o.order_no}`}>
                <div className="flex justify-between"><span className="font-num text-xs text-accent" dir="ltr">{o.order_ref || `#${o.order_no}`}</span>
                  <span className="text-xs text-accent" data-testid={`admin-order-status-${o.order_no}`}>{LABELS[o.status] || o.status}</span></div>
                <div className="text-sm mt-1">{o.customer_name} · <span dir="ltr" className="text-text-muted">{o.phone}</span></div>
                <div className="text-xs text-text-muted">{o.destination?.country} / {o.destination?.region} · {o.payment_method}</div>
                <div className="flex justify-between items-center mt-2"><span className="text-sm">{formatKWD(o.total)}</span>
                  <button onClick={() => { setOpen(o); setNs(""); }} data-testid={`order-manage-${o.order_no}`} className="text-accent underline text-xs">إدارة</button></div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="bg-white border border-border overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-surface text-text-muted text-xs">
                <tr><th className="text-start p-3">رقم الطلب</th><th className="text-start p-3">العميلة</th><th className="text-start p-3">الوجهة</th><th className="text-start p-3">الدفع</th><th className="text-start p-3">الحالة</th><th className="text-start p-3">الإجمالي</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-t border-border" data-testid={`admin-order-${o.order_no}`}>
                    <td className="p-3 font-num text-xs" dir="ltr">{o.order_ref || `#${o.order_no}`}</td>
                    <td className="p-3">{o.customer_name}</td>
                    <td className="p-3 text-text-muted">{o.destination?.country} / {o.destination?.region}</td>
                    <td className="p-3">{o.payment_method}</td>
                    <td className="p-3"><span className="text-accent" data-testid={`admin-order-status-${o.order_no}`}>{LABELS[o.status] || o.status}</span></td>
                    <td className="p-3">{formatKWD(o.total)}</td>
                    <td className="p-3 text-end"><button onClick={() => { setOpen(o); setNs(""); }} data-testid={`order-manage-${o.order_no}`} className="text-accent underline text-xs">إدارة</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <p className="p-8 text-center text-text-muted text-sm">لا توجد طلبات مطابقة</p>}
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(null)}>
          <div className="bg-white w-full max-w-lg my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="order-modal">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display text-2xl" dir="ltr">{open.order_ref || `طلب #${open.order_no}`}</h3>
              <button onClick={() => setOpen(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-text-muted mb-1">{open.customer_name} · <span dir="ltr">{open.phone}</span></p>
            <p className="text-sm text-text-muted mb-4">{open.destination?.country} / {open.destination?.region} — {open.destination?.address}</p>

            {open.items.map((it, i) => (
              <div key={i} className="border border-border p-3 mb-2 text-sm">
                <div className="flex justify-between"><span>{it.name_ar}{it.color ? ` · ${it.color_ar || it.color}` : ""}</span><span>{formatKWD(it.line_total)}</span></div>
                <Measurements it={it} />
              </div>
            ))}
            <div className="text-sm flex justify-between border-t border-border pt-2 mt-2"><span>التوصيل</span><span>{formatKWD(open.delivery_fee)}</span></div>
            <div className="text-sm flex justify-between font-medium"><span>الإجمالي</span><span>{formatKWD(open.total)}</span></div>

            {open.status === "AWAITING_CASH_PAYMENT" && (
              <button onClick={() => confirmCash(open)} data-testid="confirm-cash-btn" className="w-full mt-4 bg-[#141110] text-white py-3 text-sm">تأكيد استلام المبلغ نقداً</button>
            )}

            <div className="mt-5">
              <div className="text-xs text-text-muted mb-2">تحديث الحالة (الحالية: {LABELS[open.status]})</div>
              <div className="flex gap-2">
                <select value={ns} onChange={(e) => setNs(e.target.value)} data-testid="order-status-select" className="flex-1 border border-border px-2 py-2 text-sm">
                  <option value="">— اختر —</option>
                  {NEXT.map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
                </select>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ملاحظة / سبب" className="flex-1 border border-border px-2 py-2 text-sm" />
                <button onClick={() => setStatus(open)} data-testid="order-status-save" className="bg-[#141110] text-white px-4 text-sm">حفظ</button>
              </div>
            </div>

            <div className="mt-5">
              <div className="text-xs text-text-muted mb-2">سجل الحالات</div>
              <div className="space-y-1 max-h-40 overflow-y-auto text-xs">
                {(open.status_history || []).map((h, i) => (
                  <div key={i} className="flex justify-between border-b border-border pb-1">
                    <span>{LABELS[h.status] || h.status} {h.note && `· ${h.note}`}</span>
                    <span className="text-text-muted">{new Date(h.at).toLocaleString("ar-KW")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
